import {
  Box,
  Checkbox,
  CollapsibleCard,
  Menu,
  MenuItem,
  Button as SButton,
  DatePicker,
  Stack,
  TimeInput,
  Tooltip,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { CheckCircleIcon, InformationIcon, MenuDownIcon } from '@atgfr8/icons';
import {
  BasicModal,
  Button,
  Map,
  SelectInputFilled,
  ToastType,
  openToast,
} from '@components';
import { Coordinate } from '@components/Map/Map';
import { FeatureFlags } from '@constants/featureFlags';
import { useParams } from '@solidjs/router';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { AiTrackingStatus, nextCheckCallByStatus } from '@store/loadboard';
import { closeModal, openModal } from '@store/modals';
import {
  TrackingProviders,
  aiTrackingStatus,
  copyShipperUrl,
  disableTracking,
  enableTracking,
  getStopCoordinates,
  getTrackingData,
  orderStore,
  postNextCheckCall,
  updateLoadPropertyAtIndex,
} from '@store/orders';
import { ILoadComments } from '@store/orders/types';
import { TrackingDataPayload } from '@typeDefinitions/loadTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { timeZoneAbbreviation } from '@utils/dateFormat';
import { buildPcMilerZipLocation } from '@utils/pcMilerLocation';
import { handleToast } from '@utils/utils';
import {
  getLoadStatusItemsForMode,
  trackingOptions,
  truckerToolsTrackingOptions,
} from '@views/order/constants';
import { DateTime } from 'luxon';
import { For, Show, createEffect, createSignal } from 'solid-js';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { markOrderLoadFieldChangeSource } from '@changeTracking/pages/orderDetails/formChangeTracking';

import { CustomerTracking } from '../../customerTracking';
import { getLoadColor } from '../../header/constants';
import { subTextStyle } from '../loads/styles';
import { CustomerUpdateModal } from './CustomerUpdateModal';
import { TrackingCard } from './TrackingCard';
import classes from './classes';
import styles from './styles';
import { trackingToComment } from './utils';

export interface IZip {
  Address: {
    Zip: string;
    CountryAbbreviation?: string;
    CountryPostalFilter?: number;
  };
}

export interface ICityState {
  Address: {
    City: string;
    State: string;
  };
}

export interface trackingProps {
  loading?: boolean;
}

export const Tracking = (props: trackingProps) => {
  const [buttonRef, setButtonRef] = createSignal<HTMLButtonElement>();
  const [nextCheckCallDateUtc, setNextCheckCallDateUtc] =
    createSignal<string>();

  const hideElectronicTrackingUpdatesByDefault = isFeatureFlagEnabled(
    FeatureFlags.HideElectronicTrackingUpdatesByDefault,
  );

  const params = useParams();
  const [comment, setComment] = createSignal<ILoadComments>();
  const [hideTrackingComments, setHideTrackingComments] = createSignal<boolean>(
    hideElectronicTrackingUpdatesByDefault(),
  );
  const [checkCallEnabled, setCheckCallEnabled] = createSignal(
    orderStore.order.loads[orderStore.activeTab.index].mode !== 'LTL',
  );

  const [isTruckerToolsTrackingEnabled, setIsTruckerToolsTrackingEnabled] =
    createSignal(false);

  createEffect(() => {
    const loadId = orderStore.order.loads[orderStore.activeTab.index]?.id;

    if (!loadId) {
      setIsTruckerToolsTrackingEnabled(false);
      return;
    }

    const trackingServiceId =
      orderStore.order.loads[orderStore.activeTab.index]?.trackingServiceId;
    if (
      trackingServiceId &&
      trackingServiceId === TrackingProviders.TruckerTools
    ) {
      setIsTruckerToolsTrackingEnabled(true);
    } else {
      setIsTruckerToolsTrackingEnabled(false);
    }
  });

  createEffect(() => {
    if (
      orderStore.order.loads[orderStore.activeTab.index]?.mode !== undefined
    ) {
      setCheckCallEnabled(
        orderStore.order.loads[orderStore.activeTab.index]?.mode !== 'LTL',
      );
    }
    if (
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      orderStore.order.loads[orderStore.activeTab.index] !== undefined &&
      orderStore.order.loads[orderStore.activeTab.index]
        .nextCheckCallDateUtc !== undefined
    ) {
      setNextCheckCallDateUtc(
        DateTime.fromISO(
          `${
            orderStore.order.loads[orderStore.activeTab.index]
              .nextCheckCallDateUtc
          }`,
          { zone: 'Etc/UTC' },
        )
          .toLocal()
          .toISO({
            includeOffset: false,
          }) ?? '',
      );
    }
  });

  const customerUpdateModalId = 'trackingCustomerUpdateModalId';

  const isCreate = !params.id;
  const serviceId =
    orderStore.order.loads[orderStore.activeTab.index]?.trackingServiceId;

  createEffect(() => {
    const stops = orderStore.order.loads[
      orderStore.activeTab.index
    ]?.stops?.filter((stop) => {
      return stop.operationType !== 'Delete';
    });
    if (stops) {
      const zipArray: IZip[] = stops.map((stop) =>
        buildPcMilerZipLocation(stop.zip, stop.countryCode),
      );
      void getStopCoordinates(zipArray);
    }
  });

  const [activeTabIndex, setActiveTabIndex] = createSignal(
    orderStore.activeTab.index,
  );

  createEffect(() => {
    setActiveTabIndex(orderStore.activeTab.index);
  });

  const initialValues = {
    driverPhone:
      orderStore.order.loads[orderStore.activeTab.index]?.driverPhoneNumber,
    loadId: orderStore.order.loads[orderStore.activeTab.index]?.id,
    trackingStartDate: new Date().toISOString(),
    settings: {
      truckNumber: '',
      trailerNumber: '',
      timezone: timeZoneAbbreviation(),
      resend: true,
    },
    trackingStopWindows:
      orderStore.order.loads[orderStore.activeTab.index]?.stops?.map((val) => {
        return { Id: val.id, EDT: '', ETA: '' };
      }) || [],
    serviceId: serviceId === null ? 0 : serviceId,
  };

  const hasTrackingData = () => {
    const trackingData =
      orderStore.order.loads[orderStore.activeTab.index].loadTracking;
    if (
      trackingData !== undefined &&
      trackingData !== null &&
      trackingData.length > 0
    ) {
      return true;
    }
    return false;
  };

  const handleTracking = async (trackingOption: string) => {
    try {
      const { id } = orderStore.order.loads[orderStore.activeTab.index];
      if (trackingOption === 'Disable') {
        await disableTracking(
          id,
          CHANGE_TRACKING_SOURCES.orderDetails.trackingDisableTracking,
        );
      } else if (trackingOption === 'Resend') {
        const resp = await enableTracking(
          initialValues as unknown as TrackingDataPayload,
          CHANGE_TRACKING_SOURCES.orderDetails
            .trackingResendTrackingInstallation,
        );

        if (resp) {
          handleToast(ToastType.Success, resp.message);
        }
      } else if (trackingOption === 'CopyShipperUrl') {
        await copyShipperUrl(id);
      }
    } catch (error) {
      handleToast(ToastType.Error, (error as Error).message);
    }
  };

  const handleDateTimeChange = (value: string, source: 'time' | 'date') => {
    const prev = nextCheckCallDateUtc() ?? '';
    const [prevDate = '', prevTime = ''] = prev.split('T');

    let next: string;
    if (source === 'time') {
      // Ignore empty/partial time so the date isn't cleared.
      next = value ? prevDate + 'T' + value : prev;
    } else {
      // Clear when the date is cleared, otherwise keep the existing time.
      next = value ? value.split('T')[0] + 'T' + (prevTime || '00:00:00') : '';
    }

    setNextCheckCallDateUtc(next);
    updateLoadPropertyAtIndex({
      nextCheckCallDateUtc:
        DateTime.fromISO(next).toUTC().toISO({
          includeOffset: false,
        }) ?? '',
    });
  };

  return (
    <CollapsibleCard
      class="mb-2"
      startTitle="Tracking"
      startTitleAction={
        <div class="mobile:hidden nonMobile:block">
          <span ref={setButtonRef} class="inline-block">
            <Button
              label="Tracking Options"
              class={classes.trackingButton}
              endIcon={<MenuDownIcon />}
              size="sm"
              sx={{ 'line-height': '22px' }}
              dataTestId="tracking-options-button"
            />
          </span>
          <Menu anchorEl={buttonRef}>
            <For
              each={
                isTruckerToolsTrackingEnabled()
                  ? truckerToolsTrackingOptions
                  : trackingOptions
              }
            >
              {(item) => (
                <MenuItem
                  class="text-[rgba(0,0,0,0.87)] text-base! font-normal whitespace-nowrap"
                  onClick={() => {
                    void handleTracking(item.value);
                  }}
                >
                  <span class="inline-flex items-center justify-center h-[22px] w-[36px] align-middle">
                    <img src={item.icon} />
                  </span>{' '}
                  {item.label}
                </MenuItem>
              )}
            </For>
          </Menu>
        </div>
      }
      endIcon={
        <Show when={!orderStore.isMobile}>
          <Tooltip
            placement="left"
            title="You can double click on any Check Call to send your customer."
            dataTestId="tracking-check-call-tooltip"
          >
            <InformationIcon class="text-[24px] text-white cursor-pointer" />
          </Tooltip>
        </Show>
      }
      loading={props.loading ?? orderStore.loading}
    >
      <div>
        <Stack direction="row" class="mb-2">
          <div class="flex-auto [&>.trimblemaps-map]:w-full!">
            {/*This is a show trick to force the map to re-render when the tab is changed*/}
            <Show when={activeTabIndex() === orderStore.activeTab.index}>
              <Map
                center={[0, 0]}
                height={500}
                width={'100%'}
                zoom={3}
                coordinates={orderStore.mapCords}
                markerPositions={
                  getTrackingData()
                    .filter((tracking) => {
                      return (
                        tracking.latitude !== undefined && tracking.longitude
                      );
                    })
                    .map((tracking) => {
                      return {
                        lng: tracking.longitude as number,
                        lat: tracking.latitude as number,
                        city: tracking.city,
                        state: tracking.state,
                        locationTime: tracking.locationTime,
                        trackingSource: tracking.trackingSource,
                      };
                    }) as unknown as Coordinate[]
                }
              />
            </Show>
          </div>
          <Show when={hasTrackingData()}>
            <Stack
              width="200px"
              direction="column"
              sx={{
                'overflow-y': 'auto',
                height: '500px',
                'padding-left': '5px',
                'padding-bottom': '2px',
              }}
              gap={0.25}
            >
              {orderStore.order.loads[orderStore.activeTab.index]
                .loadTracking &&
                getTrackingData().map((t) => {
                  return (
                    <Box
                      class="cursor-pointer"
                      onClick={() => {
                        setComment(trackingToComment(t));
                        openModal(customerUpdateModalId);
                      }}
                    >
                      <TrackingCard tracking={t} />
                    </Box>
                  );
                })}
            </Stack>
          </Show>
        </Stack>
        {checkCallEnabled() ? (
          <div>
            <div class="text-[#123b50] font-normal text-base font-roboto">
              Schedule Check Calls
            </div>
            <div class="flex flex-wrap items-center gap-2">
              <Box style={{ width: '140px' }}>
                <SelectInputFilled
                  backgroundColor={getLoadColor(
                    orderStore.order.loads[orderStore.activeTab.index].status,
                  )}
                  onChange={(value) => {
                    markOrderLoadFieldChangeSource(
                      'status',
                      CHANGE_TRACKING_SOURCES.orderDetails.trackingLoadStatus,
                    );
                    updateLoadPropertyAtIndex({ status: value });
                  }}
                  label="Load Status"
                  menuItems={getLoadStatusItemsForMode(
                    orderStore.order.loads[orderStore.activeTab.index].mode,
                    orderStore.order.loads[orderStore.activeTab.index]
                      .useStatusStateMachine,
                    {
                      hasCarrier: Boolean(
                        orderStore.order.loads[orderStore.activeTab.index]
                          .carrierId,
                      ),
                    },
                  )}
                  value={
                    orderStore.order.loads[orderStore.activeTab.index].status
                  }
                  dataTestId={formatDataTestId('Load Status', 'selectInput')}
                />
              </Box>
              <Box>
                <Box
                  borderRadius="4px"
                  padding="4px 7px"
                  backgroundColor="#f0f0f0"
                  height="38px"
                  width="140px"
                >
                  <Typography
                    variant="body1"
                    component="div"
                    sxProps={{
                      ...subTextStyle,
                      textAlign: 'left',
                      marginBottom: '-6px',
                    }}
                  >
                    Next Check Call
                  </Typography>
                  <Box class="text-sm">
                    {
                      nextCheckCallByStatus[
                        orderStore.order.loads[orderStore.activeTab.index]
                          .status ?? 'None'
                      ]
                    }
                  </Box>
                </Box>
              </Box>
              <Box maxWidth="150px">
                {/*
                 * There's a weird reactivity issue here where the value of `isReadOnly` will never get
                 * passed to the `DatePicker`. So I added this `Show` trick to get around that for now.
                 */}

                <DatePicker
                  handleChange={(value: string) =>
                    handleDateTimeChange(value, 'date')
                  }
                  value={nextCheckCallDateUtc()}
                  label="Next Call Date"
                  placeholder="MM/DD/YYYY"
                  class="[&>.MuiFormControl-root]:p-0!"
                  dataTestId={'next-call-date-datepicker'}
                />
              </Box>
              <Box width="150px">
                <TimeInput
                  handleChange={(value: string) => {
                    if (nextCheckCallDateUtc() === undefined) {
                      return;
                    }
                    handleDateTimeChange(value, 'time');
                  }}
                  value={nextCheckCallDateUtc()?.split('T')[1] ?? ''}
                  label="Next Call Time"
                  iconLabelDataTestId="next-call-time-iconLabel"
                  popOverDataTestId="next-call-time-popover"
                  dataTestId="next-call-time-input"
                />
              </Box>
              <SButton
                sx={{ height: '37px' }}
                variant="outlined"
                color="primary"
                dataTestId="tracking-schedule-check-call-button"
                onClick={async () => {
                  const load =
                    orderStore.order.loads[orderStore.activeTab.index];
                  const localDate = load.nextCheckCallDateUtc;

                  if (localDate === undefined) {
                    openToast({
                      type: ToastType.Error,
                      position: 'top-right',
                      message: 'A valid date time is required',
                      duration: 2500,
                    });
                    return;
                  }

                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  if (load !== undefined) {
                    try {
                      const res = await postNextCheckCall(
                        load.id,
                        localDate as string,
                      );

                      if (!res) {
                        handleToast(
                          ToastType.Error,
                          'There was an issue updating the next check call date',
                        );
                      } else {
                        handleToast(ToastType.Success, 'Check call scheduled');
                      }
                    } catch (error) {
                      handleToast(ToastType.Error, (error as Error).message);
                    }
                  }
                }}
                disabled={orderStore.isReadOnly}
              >
                <span class="text-sm">SCHEDULE CHECK CALL</span>
              </SButton>
              <div class="ml-2">
                <Checkbox
                  label="Hide Electronic Tracking Updates"
                  onChange={(v) => {
                    setHideTrackingComments(v);
                  }}
                  checked={hideTrackingComments()}
                  dataTestId={'hide-electronic-tracking-updates-checkboxInput'}
                  size="xs"
                  color="info"
                  class="ml-[-9px]"
                />
              </div>
              <Show when={aiTrackingStatus() === AiTrackingStatus.Enabled}>
                <div class="flex items-center gap-2 bg-armstrong-blue text-white px-2 py-1 rounded-lg">
                  <div>AI Enabled</div>
                  <CheckCircleIcon class="text-white" />
                </div>
              </Show>
            </div>
          </div>
        ) : (
          <div>
            <div class="text-[#123b50] font-normal text-base font-roboto ">
              Tracking Calls
            </div>
            <div class="flex items-center">
              <Box style={{ width: '140px' }}>
                <SelectInputFilled
                  backgroundColor={getLoadColor(
                    orderStore.order.loads[orderStore.activeTab.index].status,
                  )}
                  onChange={(value) => {
                    markOrderLoadFieldChangeSource(
                      'status',
                      CHANGE_TRACKING_SOURCES.orderDetails.trackingLoadStatus,
                    );
                    updateLoadPropertyAtIndex({ status: value });
                  }}
                  label="Load Status"
                  menuItems={getLoadStatusItemsForMode(
                    orderStore.order.loads[orderStore.activeTab.index].mode,
                    orderStore.order.loads[orderStore.activeTab.index]
                      .useStatusStateMachine,
                    {
                      hasCarrier: Boolean(
                        orderStore.order.loads[orderStore.activeTab.index]
                          .carrierId,
                      ),
                    },
                  )}
                  value={
                    orderStore.order.loads[orderStore.activeTab.index].status
                  }
                  dataTestId={formatDataTestId(
                    'Load Status Tracking Calls',
                    'selectInput',
                  )}
                />
              </Box>
              <div class="ml-2">
                <Checkbox
                  label="Hide Electronic Tracking Updates"
                  onChange={(v) => {
                    setHideTrackingComments(v);
                  }}
                  checked={hideTrackingComments()}
                  dataTestId={'hide-electronic-tracking-updates-checkboxInput'}
                  size="xs"
                  color="info"
                  class="ml-[-9px]"
                />
              </div>
            </div>
          </div>
        )}
        <Show when={!isCreate}>
          <CustomerTracking
            activeCarrierTab={orderStore.activeTab.index}
            hideTrackingComments={hideTrackingComments()}
            addCommentChangeTrackingSource={
              CHANGE_TRACKING_SOURCES.orderDetails.trackingAddComment
            }
            deleteCommentChangeTrackingSource={
              CHANGE_TRACKING_SOURCES.orderDetails.trackingDeleteComment
            }
            sendCustomerUpdateChangeTrackingSource={
              CHANGE_TRACKING_SOURCES.orderDetails.trackingSendCustomerUpdate
            }
            updateVisibilityChangeTrackingSource={
              CHANGE_TRACKING_SOURCES.orderDetails.trackingUpdateVisibility
            }
          />
        </Show>
      </div>
      <BasicModal
        id={customerUpdateModalId}
        title="Send Customer Update"
        footer={false}
        onClose={() => {
          closeModal(customerUpdateModalId);
        }}
        modalStyles={styles.basicModal}
        width="820px"
        noCloseOnClickOutside={false}
      >
        <CustomerUpdateModal
          comment={comment()}
          excludeDate
          changeTrackingSource={
            CHANGE_TRACKING_SOURCES.orderDetails.trackingSendCustomerUpdate
          }
        />
      </BasicModal>
    </CollapsibleCard>
  );
};
